import { useState, useEffect, useCallback, useMemo } from "react";
import axios from "axios";
import { toast } from "sonner";
import { ChevronLeft, ChevronRight, Loader2, Save, Plus, Trash2, X, Info, Zap } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CAT_LABEL = {
  low_season: "Base", high_season: "Alta stagione", weekend: "Weekend",
  period: "Periodo", event: "Evento", manual: "Manuale", dynamic: "Dinamico",
};
const CAT_COLOR = {
  low_season: "text-[#5C6773]",
  high_season: "text-[#0D253A]",
  weekend: "text-amber-700",
  period: "text-[#1E5F74]",
  event: "text-[#C86D51]",
  manual: "text-fuchsia-700",
  dynamic: "text-emerald-700",
};

const toISO = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;

const AdminDailyPricing = ({ authHeaders }) => {
  const today = useMemo(() => new Date(), []);
  const [offset, setOffset] = useState(0);
  const [days, setDays] = useState([]);
  const [suggestionsMap, setSuggestionsMap] = useState({});
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState(null); // ISO string
  const [panel, setPanel] = useState({ price: "", note: "" });
  const [dyn, setDyn] = useState(null);
  const [saving, setSaving] = useState(false);

  const monthStart = useMemo(() => {
    const d = new Date(today.getFullYear(), today.getMonth() + offset, 1);
    return d;
  }, [today, offset]);
  const monthEnd = useMemo(() => new Date(monthStart.getFullYear(), monthStart.getMonth() + 1, 0), [monthStart]);
  const monthLabel = useMemo(() => new Intl.DateTimeFormat("it-IT", { month: "long", year: "numeric" }).format(monthStart), [monthStart]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [{ data: dp }, { data: dc }, { data: sg }] = await Promise.all([
        axios.get(`${API}/admin/daily-prices`, {
          headers: authHeaders,
          params: { from_: toISO(monthStart), to: toISO(monthEnd) },
        }),
        axios.get(`${API}/admin/dynamic-pricing`, { headers: authHeaders }),
        axios.get(`${API}/admin/pricing-suggestions`, {
          headers: authHeaders,
          params: { from_: toISO(monthStart), to: toISO(monthEnd) },
        }).catch(() => ({ data: { suggestions: [] } })),
      ]);
      setDays(dp.days || []);
      setDyn(dc);
      const smap = {};
      (sg.suggestions || []).forEach((s) => { smap[s.date] = s; });
      setSuggestionsMap(smap);
    } catch { toast.error("Impossibile caricare le tariffe"); }
    finally { setLoading(false); }
  }, [authHeaders, monthStart, monthEnd]);

  useEffect(() => { load(); }, [load]);

  const openDay = (d) => {
    setSelected(d.date);
    setPanel({
      price: d.category === "manual" ? String(d.price) : "",
      note: d.meta?.note || "",
    });
  };

  const saveOverride = async () => {
    const p = Number(panel.price);
    if (!p || p <= 0) { toast.error("Inserisci un prezzo > 0"); return; }
    setSaving(true);
    try {
      await axios.post(`${API}/admin/daily-overrides`, {
        date: selected, price: p, note: panel.note || "",
      }, { headers: authHeaders });
      toast.success(`Prezzo del ${selected} impostato a € ${p.toFixed(2)}`);
      setSelected(null);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Salvataggio fallito"); }
    finally { setSaving(false); }
  };

  const removeOverride = async () => {
    if (!window.confirm(`Rimuovere l'override del ${selected} e tornare al prezzo automatico?`)) return;
    setSaving(true);
    try {
      await axios.delete(`${API}/admin/daily-overrides/${selected}`, { headers: authHeaders });
      toast.success("Override rimosso");
      setSelected(null);
      load();
    } catch (e) {
      if (e.response?.status === 404) { toast.info("Nessun override da rimuovere"); setSelected(null); load(); }
      else toast.error("Rimozione fallita");
    } finally { setSaving(false); }
  };

  const saveDynamic = async () => {
    setSaving(true);
    try {
      await axios.patch(`${API}/admin/dynamic-pricing`, {
        ...dyn,
        lead_time_bands: (dyn.lead_time_bands || []).map((b) => ({
          min_days: Number(b.min_days), max_days: Number(b.max_days), delta_pct: Number(b.delta_pct),
        })),
        nearby_thresholds: (dyn.nearby_thresholds || []).map((t) => ({
          min_occupied: Number(t.min_occupied), delta_pct: Number(t.delta_pct),
        })),
        min_price_value: Number(dyn.min_price_value),
        max_price_value: Number(dyn.max_price_value),
      }, { headers: authHeaders });
      toast.success("Motore dinamico salvato");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Salvataggio fallito"); }
    finally { setSaving(false); }
  };

  const applySuggestion = async (iso) => {
    const s = suggestionsMap[iso];
    if (!s) return;
    setSaving(true);
    try {
      await axios.post(`${API}/admin/daily-overrides`, {
        date: iso, price: s.suggested_price,
        note: `Suggerito dallo storico (${s.delta_pct > 0 ? "+" : ""}${s.delta_pct}%)`,
      }, { headers: authHeaders });
      toast.success(`Prezzo del ${iso}: € ${s.suggested_price.toFixed(2)} applicato`);
      setSelected(null);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Applicazione fallita"); }
    finally { setSaving(false); }
  };

  const dayTooltip = (d) => {
    if (d.category === "manual") {
      const note = d.meta?.note ? ` · "${d.meta.note}"` : "";
      return `Prezzo manuale € ${d.price.toFixed(2)}${note}`;
    }
    if (d.category === "event" || d.category === "period") {
      return `${d.meta?.name || (d.category === "event" ? "Evento" : "Periodo")} · € ${d.price.toFixed(2)}`;
    }
    if (d.category === "dynamic") {
      const base = d.meta?.base_rate ?? 0;
      const w = d.meta?.weekend_supp || 0;
      const preDyn = d.meta?.pre_dynamic_rate ?? base;
      const lead = d.meta?.lead_pct || 0;
      const near = d.meta?.nearby_pct || 0;
      const delta = d.price - preDyn;
      const sign = delta >= 0 ? "+" : "−";
      const parts = [`€ ${base.toFixed(0)} base`];
      if (w) parts.push(`+ € ${w.toFixed(0)} weekend`);
      parts.push(`${sign} € ${Math.abs(delta).toFixed(2)} dinamico (${lead > 0 ? "+" : ""}${lead}% lead × ${near > 0 ? "+" : ""}${near}% vicini)`);
      return parts.join(" ");
    }
    if (d.category === "weekend") {
      const base = d.meta?.base_rate ?? 0;
      const w = d.meta?.weekend_supp || 0;
      const sign = w >= 0 ? "+" : "−";
      return `€ ${base.toFixed(0)} base ${sign} € ${Math.abs(w).toFixed(0)} weekend`;
    }
    return d.category === "high_season" ? `€ ${d.price.toFixed(0)} · alta stagione` : `€ ${d.price.toFixed(0)} · base`;
  };

  // Build calendar grid cells with leading blanks
  const cells = useMemo(() => {
    if (!days.length) return [];
    const first = new Date(`${days[0].date}T12:00:00`);
    const leading = (first.getDay() + 6) % 7; // Mon=0
    return [...Array(leading).fill(null), ...days];
  }, [days]);

  const selectedDay = days.find((d) => d.date === selected);

  const inp = "w-full px-3 py-2 rounded-lg bg-white border border-[#0D253A]/15 text-sm";
  const weekdays = ["L", "M", "M", "G", "V", "S", "D"];

  return (
    <div data-testid="studio-daily-pricing" className="grid grid-cols-1 lg:grid-cols-[1fr_380px] gap-6">
      <div>
        {/* Month navigator */}
        <div className="bg-white rounded-2xl border border-[#0D253A]/10 p-5">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h3 className="font-display text-xl text-[#0D253A] capitalize">{monthLabel}</h3>
              <p className="text-xs text-[#5C6773] mt-0.5">Clicca su una notte per impostare un prezzo manuale.</p>
            </div>
            <div className="flex items-center gap-2">
              <button data-testid="daily-prev" onClick={() => setOffset(Math.max(0, offset - 1))} disabled={offset === 0}
                className="p-2 rounded-full border border-[#0D253A]/15 hover:border-[#C86D51] hover:text-[#C86D51] disabled:opacity-30">
                <ChevronLeft className="w-4 h-4" />
              </button>
              <button data-testid="daily-next" onClick={() => setOffset(Math.min(11, offset + 1))} disabled={offset >= 11}
                className="p-2 rounded-full border border-[#0D253A]/15 hover:border-[#C86D51] hover:text-[#C86D51] disabled:opacity-30">
                <ChevronRight className="w-4 h-4" />
              </button>
            </div>
          </div>

          {loading ? (
            <p className="text-sm text-[#5C6773] flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> Carico…</p>
          ) : (
            <div className="grid grid-cols-7 gap-1.5">
              {weekdays.map((w, i) => (
                <span key={i} className="text-[10px] font-semibold uppercase text-[#5C6773] text-center py-1">{w}</span>
              ))}
              {cells.map((d, i) => {
                if (!d) return <span key={i} />;
                const isSel = selected === d.date;
                const suggestion = suggestionsMap[d.date];
                return (
                  <button
                    key={d.date}
                    data-testid={`daily-cell-${d.date}`}
                    onClick={() => openDay(d)}
                    title={dayTooltip(d)}
                    className={`relative aspect-square flex flex-col items-center justify-center rounded-lg border transition-colors p-1 ${
                      isSel ? "border-[#C86D51] bg-[#C86D51]/10" : "border-[#0D253A]/10 hover:border-[#C86D51]/50"
                    } ${d.occupied ? "opacity-70" : ""}`}
                  >
                    <span className="text-[11px] font-semibold text-[#0D253A]">{Number(d.date.slice(8))}</span>
                    <span className={`text-[13px] font-display leading-tight ${CAT_COLOR[d.category] || "text-[#0D253A]"}`}>
                      € {Number(d.price).toFixed(0)}
                    </span>
                    <span className="text-[8px] uppercase tracking-wider text-[#5C6773]">{CAT_LABEL[d.category] || d.category}</span>
                    {d.occupied && <span className="w-1 h-1 rounded-full bg-[#C86D51] mt-0.5" />}
                    {suggestion && !d.occupied && d.category !== "manual" && (
                      <span
                        data-testid={`daily-suggestion-dot-${d.date}`}
                        className={`absolute top-1 right-1 w-2 h-2 rounded-full ${suggestion.delta_pct > 0 ? "bg-emerald-500" : "bg-sky-500"}`}
                        title={`Suggerito ${suggestion.delta_pct > 0 ? "+" : ""}${suggestion.delta_pct}% → € ${suggestion.suggested_price.toFixed(2)}`}
                      />
                    )}
                  </button>
                );
              })}
            </div>
          )}
          <div className="mt-4 flex flex-wrap gap-3 text-[10px] uppercase tracking-wider text-[#5C6773]">
            {Object.entries(CAT_LABEL).map(([k, v]) => (
              <span key={k} className="inline-flex items-center gap-1">
                <span className={`w-2 h-2 rounded-full inline-block ${CAT_COLOR[k]?.replace("text-", "bg-") || "bg-[#5C6773]"}`} /> {v}
              </span>
            ))}
          </div>
        </div>

        {/* Dynamic engine editor */}
        {dyn && (
          <div className="mt-6 bg-white rounded-2xl border border-[#0D253A]/10 p-6">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <Zap className="w-4 h-4 text-[#C86D51]" />
                <h3 className="font-display text-lg text-[#0D253A]">Motore dinamico</h3>
              </div>
              <label className="inline-flex items-center gap-2 text-sm">
                <input data-testid="dyn-enabled" type="checkbox" checked={!!dyn.enabled}
                  onChange={(e) => setDyn({ ...dyn, enabled: e.target.checked })} />
                {dyn.enabled ? "Attivo" : "Spento"}
              </label>
            </div>
            <p className="text-xs text-[#5C6773] mb-4 flex items-start gap-2">
              <Info className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" />
              Regola i prezzi in base a quanto manca al check-in e a quante notti vicine sono già prenotate. Non tocca eventi, periodi speciali e prezzi manuali. I due delta si moltiplicano in cascata.
            </p>

            {/* Lead time bands */}
            <p className="text-xs font-semibold uppercase tracking-wider text-[#5C6773] mb-2">Bande lead time (giorni al check-in → delta %)</p>
            <div className="space-y-2 mb-4">
              {(dyn.lead_time_bands || []).map((b, i) => (
                <div key={i} data-testid={`dyn-band-${i}`} className="grid grid-cols-[1fr_1fr_1fr_auto] gap-2 items-center">
                  <input type="number" min="0" value={b.min_days} onChange={(e) => {
                    const arr = [...dyn.lead_time_bands]; arr[i] = { ...arr[i], min_days: e.target.value }; setDyn({ ...dyn, lead_time_bands: arr });
                  }} className={inp} placeholder="da (gg)" />
                  <input type="number" min="0" value={b.max_days} onChange={(e) => {
                    const arr = [...dyn.lead_time_bands]; arr[i] = { ...arr[i], max_days: e.target.value }; setDyn({ ...dyn, lead_time_bands: arr });
                  }} className={inp} placeholder="a (gg)" />
                  <input type="number" step="0.5" value={b.delta_pct} onChange={(e) => {
                    const arr = [...dyn.lead_time_bands]; arr[i] = { ...arr[i], delta_pct: e.target.value }; setDyn({ ...dyn, lead_time_bands: arr });
                  }} className={inp} placeholder="delta %" />
                  <button type="button" onClick={() => {
                    const arr = dyn.lead_time_bands.filter((_, x) => x !== i); setDyn({ ...dyn, lead_time_bands: arr });
                  }} className="p-2 rounded-full border border-[#0D253A]/15 hover:border-[#C86D51] hover:text-[#C86D51]">
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                </div>
              ))}
              <button type="button" onClick={() => setDyn({ ...dyn, lead_time_bands: [...(dyn.lead_time_bands || []), { min_days: 0, max_days: 0, delta_pct: 0 }] })}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-dashed border-[#0D253A]/30 text-xs text-[#5C6773] hover:border-[#C86D51] hover:text-[#C86D51]">
                <Plus className="w-3.5 h-3.5" /> Aggiungi banda
              </button>
            </div>

            {/* Nearby occupancy */}
            <p className="text-xs font-semibold uppercase tracking-wider text-[#5C6773] mb-2">Occupazione vicina (3 gg prima + 3 dopo → delta %)</p>
            <div className="space-y-2 mb-4">
              {(dyn.nearby_thresholds || []).map((t, i) => (
                <div key={i} data-testid={`dyn-thresh-${i}`} className="grid grid-cols-[1fr_1fr_auto] gap-2 items-center">
                  <input type="number" min="1" max="6" value={t.min_occupied} onChange={(e) => {
                    const arr = [...dyn.nearby_thresholds]; arr[i] = { ...arr[i], min_occupied: e.target.value }; setDyn({ ...dyn, nearby_thresholds: arr });
                  }} className={inp} placeholder="min notti prenotate" />
                  <input type="number" step="0.5" value={t.delta_pct} onChange={(e) => {
                    const arr = [...dyn.nearby_thresholds]; arr[i] = { ...arr[i], delta_pct: e.target.value }; setDyn({ ...dyn, nearby_thresholds: arr });
                  }} className={inp} placeholder="delta %" />
                  <button type="button" onClick={() => {
                    const arr = dyn.nearby_thresholds.filter((_, x) => x !== i); setDyn({ ...dyn, nearby_thresholds: arr });
                  }} className="p-2 rounded-full border border-[#0D253A]/15 hover:border-[#C86D51] hover:text-[#C86D51]">
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                </div>
              ))}
              <button type="button" onClick={() => setDyn({ ...dyn, nearby_thresholds: [...(dyn.nearby_thresholds || []), { min_occupied: 1, delta_pct: 0 }] })}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-dashed border-[#0D253A]/30 text-xs text-[#5C6773] hover:border-[#C86D51] hover:text-[#C86D51]">
                <Plus className="w-3.5 h-3.5" /> Aggiungi soglia
              </button>
            </div>

            {/* Guardrails */}
            <p className="text-xs font-semibold uppercase tracking-wider text-[#5C6773] mb-2">Guardrail prezzo minimo/massimo</p>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs text-[#5C6773]">Minimo</label>
                <div className="flex gap-2">
                  <select value={dyn.min_price_kind} onChange={(e) => setDyn({ ...dyn, min_price_kind: e.target.value })} className={inp}>
                    <option value="percent">% base</option>
                    <option value="amount">€ fisso</option>
                  </select>
                  <input data-testid="dyn-min-value" type="number" step="0.5" value={dyn.min_price_value} onChange={(e) => setDyn({ ...dyn, min_price_value: e.target.value })} className={inp} />
                </div>
              </div>
              <div>
                <label className="text-xs text-[#5C6773]">Massimo</label>
                <div className="flex gap-2">
                  <select value={dyn.max_price_kind} onChange={(e) => setDyn({ ...dyn, max_price_kind: e.target.value })} className={inp}>
                    <option value="percent">% base</option>
                    <option value="amount">€ fisso</option>
                  </select>
                  <input data-testid="dyn-max-value" type="number" step="0.5" value={dyn.max_price_value} onChange={(e) => setDyn({ ...dyn, max_price_value: e.target.value })} className={inp} />
                </div>
              </div>
            </div>

            <div className="flex justify-end mt-5">
              <button data-testid="dyn-save" onClick={saveDynamic} disabled={saving}
                className="inline-flex items-center gap-2 px-6 py-3 bg-[#C86D51] text-white text-sm font-semibold rounded-full hover:bg-[#0D253A] disabled:opacity-60 transition-colors">
                {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} Salva impostazioni
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Side panel */}
      {selected && selectedDay && (
        <div data-testid="daily-side-panel" className="bg-white rounded-2xl border border-[#0D253A]/10 p-6 h-fit sticky top-24">
          <div className="flex items-start justify-between">
            <div>
              <p className="text-xs uppercase tracking-wider text-[#5C6773]">Notte del</p>
              <p className="font-display text-xl text-[#0D253A]">
                {new Intl.DateTimeFormat("it-IT", { day: "numeric", month: "long", year: "numeric" }).format(new Date(`${selected}T12:00:00`))}
              </p>
            </div>
            <button onClick={() => setSelected(null)} className="p-1.5 rounded-full text-[#5C6773] hover:text-[#C86D51]">
              <X className="w-4 h-4" />
            </button>
          </div>
          <div className="mt-4 p-3 rounded-xl bg-[#F8F5F0] border border-[#E3DED6]">
            <p className="text-[10px] uppercase tracking-wider text-[#5C6773]">Prezzo attuale</p>
            <p className={`font-display text-2xl ${CAT_COLOR[selectedDay.category] || "text-[#0D253A]"}`}>€ {Number(selectedDay.price).toFixed(2)}</p>
            <p className="text-xs text-[#5C6773]">{CAT_LABEL[selectedDay.category] || selectedDay.category}
              {selectedDay.meta?.base_rate && ` · base € ${selectedDay.meta.base_rate.toFixed(0)}, lead ${selectedDay.meta.lead_pct}%, vicini ${selectedDay.meta.nearby_pct}%`}
              {selectedDay.meta?.name && ` · ${selectedDay.meta.name}`}
              {selectedDay.category === "manual" && selectedDay.meta?.note && ` · "${selectedDay.meta.note}"`}
            </p>
          </div>
          <div className="mt-5 space-y-3">
            {suggestionsMap[selected] && selectedDay.category !== "manual" && (
              <div data-testid="daily-suggestion-box" className={`p-3 rounded-xl border ${
                suggestionsMap[selected].delta_pct > 0 ? "border-emerald-300 bg-emerald-50" : "border-sky-300 bg-sky-50"
              }`}>
                <p className="text-[10px] uppercase tracking-wider font-semibold text-[#5C6773]">Suggerimento dallo storico</p>
                <p className="text-sm text-[#0D253A] mt-1">
                  L'anno scorso <strong>{suggestionsMap[selected].last_year_nearby_booked}/7</strong> notti vicine erano
                  {suggestionsMap[selected].delta_pct > 0 ? " prenotate" : " libere"}. Prezzo suggerito:
                  <strong className="ml-1">€ {suggestionsMap[selected].suggested_price.toFixed(2)}</strong>
                  <span className="ml-1 text-xs">({suggestionsMap[selected].delta_pct > 0 ? "+" : ""}{suggestionsMap[selected].delta_pct}%)</span>
                </p>
                <button data-testid="daily-apply-suggestion" onClick={() => applySuggestion(selected)} disabled={saving}
                  className="mt-2 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-[#0D253A] text-white text-xs font-semibold hover:bg-[#C86D51] disabled:opacity-60">
                  Applica suggerito
                </button>
              </div>
            )}
            <div>
              <label className="text-xs font-semibold uppercase tracking-wider text-[#5C6773]">Prezzo manuale €</label>
              <input data-testid="daily-price-input" type="number" step="0.5" min="0" value={panel.price} onChange={(e) => setPanel({ ...panel, price: e.target.value })}
                className={inp} placeholder="es. 260" autoFocus />
            </div>
            <div>
              <label className="text-xs font-semibold uppercase tracking-wider text-[#5C6773]">Nota (privata)</label>
              <input data-testid="daily-note-input" type="text" maxLength={200} value={panel.note} onChange={(e) => setPanel({ ...panel, note: e.target.value })}
                className={inp} placeholder="es. evento privato, compleanno amici" />
            </div>
            <button data-testid="daily-save" onClick={saveOverride} disabled={saving}
              className="w-full inline-flex items-center justify-center gap-2 px-4 py-3 bg-[#C86D51] text-white text-sm font-semibold rounded-full hover:bg-[#0D253A] disabled:opacity-60 transition-colors">
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} Salva prezzo manuale
            </button>
            {selectedDay.category === "manual" && (
              <button data-testid="daily-remove" onClick={removeOverride} disabled={saving}
                className="w-full inline-flex items-center justify-center gap-2 px-4 py-2.5 border border-[#0D253A]/20 text-[#0D253A]/80 text-xs font-semibold rounded-full hover:border-[#C86D51] hover:text-[#C86D51]">
                <Trash2 className="w-3.5 h-3.5" /> Rimuovi override → prezzo automatico
              </button>
            )}
          </div>
          <p className="mt-4 text-[11px] text-[#5C6773] leading-relaxed">
            Il prezzo manuale vince su ogni altra regola (motore dinamico, evento, weekend, base) finché non lo rimuovi. Non è soggetto ai guardrail min/max.
          </p>
        </div>
      )}
    </div>
  );
};

export default AdminDailyPricing;
